import React, { useState, useEffect, FC } from 'react';
import {
  Box,
  Typography,
  Modal,
  Button,
  TextField,
  Chip,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import ClearIcon from '@mui/icons-material/Clear';
import { ModalChooseCategory } from '../modalChooseCategory';
import { showError, showSuccess } from '../../notifications';
import api from '../../../api/axiosInstance';
import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import { sanitizeMoneyInput } from '../../../utils/regex';
import { globalSearch, getOperationFromSearch, SearchItem } from '../../../api/searchApi';
import { formatCurrency } from '../../../utils/formatMoney';
import {
  OPERATION_NAME_LIMITS,
  OPERATION_DESCRIPTION_LIMITS,
  TAG_NAME_LIMITS,
  SEARCH_LIMITS,
} from '../../../utils/validationLimits';
export interface OperationData {
  id?: number;
  type: 'income' | 'expense';
  amount?: string | number;
  name: string;
  description?: string;
  date: string;
  tagincomes?: { id: number; name: string }[];
  tagexpenses?: { id: number; name: string }[];
  categoryincomeId?: number | null;
  categoryexpenseId?: number | null;
  categoryincome?: { id: number; name: string };
  categoryexpense?: { id: number; name: string };
}

interface Category {
  id: number;
  name: string;
  parentId: number | null;
}

interface Tag {
  id: number;
  name: string;
}

interface ModalOperationsProps {
  mode?: 'create' | 'edit' | 'copy';
  typeItem?: 'income' | 'expense';
  open: boolean;
  initialData?: OperationData;
  onClose: () => void;
  onSubmit: (data: OperationData) => void;
}

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

const ModalOperations: FC<ModalOperationsProps> = ({
  mode = 'create',
  typeItem,
  open,
  initialData,
  onClose,
  onSubmit,
}) => {
  const queryClient = useQueryClient();

  const [type, setType] = useState<'income' | 'expense'>(typeItem ?? 'income');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState<Date>(new Date());
  const [tags, setTags] = useState<Tag[]>([]);
  const [note, setNote] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [openCategory, setOpenCategory] = useState(false);
  const [touchedName, setTouchedName] = useState(false);
  const [touchedAmount, setTouchedAmount] = useState(false);
  const firstOpenRef = React.useRef(true);

  const [editTagOpen, setEditTagOpen] = useState(false);
  const [editTagValue, setEditTagValue] = useState('');
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [editTouched, setEditTouched] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [templateQuery, setTemplateQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<SearchItem | null>(null);
  const [touchedNote, setTouchedNote] = useState(false);
  const deferredTemplateQuery = React.useDeferredValue(templateQuery);
  const templateSearch = deferredTemplateQuery.trim();

  const isEditMode = mode === 'edit';

  const applyOperationTemplate = async (item: SearchItem | null) => {
    if (!item || item.kind !== 'operation') return;

    const fullOperation = await getOperationFromSearch(item);

    setType(fullOperation.type);
    setName(fullOperation.name);
    setAmount(
      typeof fullOperation.amount === 'number'
        ? String(fullOperation.amount)
        : sanitizeMoneyInput(String(fullOperation.amount), 2),
    );
    setNote(fullOperation.description ?? '');

    if (fullOperation.type === 'income') {
      setTags((fullOperation.tagincomes ?? []).map(tag => ({ id: tag.id, name: tag.name })));

      if (fullOperation.categoryincome) {
        setSelectedCategory({
          id: String(fullOperation.categoryincome.id),
          label: fullOperation.categoryincome.name,
        });
      } else {
        setSelectedCategory(null);
      }
    } else {
      setTags((fullOperation.tagexpenses ?? []).map(tag => ({ id: tag.id, name: tag.name })));

      if (fullOperation.categoryexpense) {
        setSelectedCategory({
          id: String(fullOperation.categoryexpense.id),
          label: fullOperation.categoryexpense.name,
        });
      } else {
        setSelectedCategory(null);
      }
    }

    setTouchedName(false);
    setTouchedAmount(false);
    setTouchedNote(false);
  };

  const openEditTagModal = (tag: Tag) => {
    setEditingTag(tag);
    setEditTagValue(tag.name);
    setEditTouched(false);
    setEditTagOpen(true);
  };

  const closeEditTagModal = () => {
    if (editLoading) return;
    setEditTagOpen(false);
    setEditingTag(null);
    setEditTagValue('');
    setEditTouched(false);
  };

  const trimmedEditTagValue = editTagValue.trim();
  const isEditNameValid =
    trimmedEditTagValue.length >= TAG_NAME_LIMITS.min &&
    trimmedEditTagValue.length <= TAG_NAME_LIMITS.max;

  const fetchType = initialData?.type ?? type;

  const { data: templateOptions = [], isLoading: templateLoading } = useQuery<SearchItem[], Error>({
    queryKey: ['operation-templates', fetchType, templateSearch],
    enabled: open && !isEditMode && templateSearch.length >= 2,

    queryFn: async () => {
      const items = await globalSearch({
        q: templateSearch,
        type: fetchType,
        limit: 8,
      });

      return items.filter(item => item.kind === 'operation');
    },
  });

  const { data: allCategories = [] } = useQuery<Category[], Error>({
    queryKey: ['categories', fetchType],
    queryFn: () =>
      api
        .get<Category[]>(fetchType === 'income' ? '/categories-income' : '/categories-expense')
        .then(r => r.data),
    enabled: open,
  });

  const { data: allTags = [] } = useQuery<Tag[], Error>({
    queryKey: ['tags', fetchType],
    queryFn: () =>
      api.get<Tag[]>(fetchType === 'income' ? '/tag-income' : '/tag-expense').then(r => r.data),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      firstOpenRef.current = true;
      return;
    }

    setTouchedName(false);
    setTouchedAmount(false);
    setTouchedNote(false);

    if (initialData) {
      setType(initialData.type);
      setSelectedTemplate(null);
      setTemplateQuery('');

      setName(initialData.name);
      setAmount(
        typeof initialData.amount === 'number'
          ? String(initialData.amount)
          : sanitizeMoneyInput(String(initialData.amount), 2),
      );
      setDate(new Date(initialData.date));

      const tagSource =
        (initialData.type === 'income' && initialData.tagincomes) ||
        (initialData.type === 'expense' && (initialData as any).tagexpenses) ||
        [];
      setTags(tagSource.map((t: Tag) => ({ id: t.id, name: t.name })));

      setNote(initialData.description ?? '');

      const cid = initialData.categoryincomeId ?? initialData.categoryexpenseId;
      if (cid != null) {
        const found = allCategories.find(c => c.id === cid);
        setSelectedCategory(
          found ? { id: String(found.id), label: found.name } : { id: String(cid), label: '' },
        );
      } else {
        setSelectedCategory(null);
      }
    } else if (firstOpenRef.current) {
      setType(typeItem ?? 'income');
      setName('');
      setAmount('');
      setDate(new Date());
      setTags([]);
      setNote('');
      setSelectedCategory(null);
      firstOpenRef.current = false;
    }
  }, [open, initialData, allCategories, typeItem]);

  const mutation = useMutation<OperationData, Error, OperationData>({
    mutationFn: data => {
      const base = data.type === 'income' ? '/incomes' : '/expenses';
      if (data.id != null) {
        return api.patch<OperationData>(`${base}/${data.id}`, data).then(r => r.data);
      }
      return api.post<OperationData>(base, data).then(r => r.data);
    },
    onSuccess: result => {
      showSuccess(isEditMode ? 'Операция обновлена' : 'Операция успешно добавлена');

      const listKey = result.type === 'income' ? 'incomes' : 'expenses';
      queryClient.invalidateQueries({ queryKey: [listKey], exact: true });
      onSubmit(result);
      onClose();

      queryClient.invalidateQueries({ queryKey: ['all'] });
      queryClient.invalidateQueries({ queryKey: ['income'] });
      queryClient.invalidateQueries({ queryKey: ['expense'] });

      queryClient.invalidateQueries({ queryKey: ['categories', fetchType] });
      queryClient.invalidateQueries({ queryKey: ['tags', fetchType] });
    },
    onError: err => {
      if (isAxiosError(err)) showError(err.response?.data?.message ?? err.message);
      else showError(err.message);
    },
  });

  const decimal2 = /^\d+(\.\d{1,2})?$/;
  const trimmedName = name.trim();
  const trimmedNote = note.trim();

  const isNameValid =
    trimmedName.length >= OPERATION_NAME_LIMITS.min &&
    trimmedName.length <= OPERATION_NAME_LIMITS.max;

  const isNoteValid = trimmedNote.length <= OPERATION_DESCRIPTION_LIMITS.max;
  const isAmountValid = amount.trim() !== '' && decimal2.test(amount);
  const isFormValid = isNameValid && isAmountValid && isNoteValid;

  const handleDeleteTag = (tag: Tag) => {
    setTags(prev => prev.filter(t => t !== tag));
  };

  const handleSubmit = () => {
    if (!isFormValid || mutation.isPending) {
      setTouchedName(true);
      setTouchedAmount(true);
      setTouchedNote(true);
      return;
    }

    const num = parseFloat(amount);
    if (!Number.isFinite(num)) {
      showError('Введите корректную сумму');
      return;
    }
    const basePayload: Partial<OperationData> = {
      id: isEditMode ? initialData?.id : undefined,
      type: initialData?.type ?? type,
      name: name.trim(),
      amount: sanitizeMoneyInput(amount, 2),
      description: note.trim(),
      date: date.toISOString(),
    };

    let payload: OperationData;
    if ((initialData?.type ?? type) === 'income') {
      payload = {
        ...basePayload,
        tagincomes: tags,
        categoryincomeId: selectedCategory ? Number(selectedCategory.id) : null,
      } as OperationData;
    } else {
      payload = {
        ...basePayload,
        tagexpenses: tags,
        categoryexpenseId: selectedCategory ? Number(selectedCategory.id) : null,
      } as OperationData;
    }

    mutation.mutate(payload);
  };

  const handleSaveTagName = async () => {
    if (!editingTag) return;

    const newName = editTagValue.trim();
    setEditTouched(true);

    if (newName.length < TAG_NAME_LIMITS.min || newName.length > TAG_NAME_LIMITS.max) {
      return;
    }
    setEditTouched(true);

    if (!newName) return;

    const isDuplicate = tags.some(t => t.name === newName && t !== editingTag);
    if (isDuplicate) {
      showError('Такой тег уже добавлен');
      return;
    }

    try {
      setEditLoading(true);

      if (editingTag.id !== 0) {
        await api.patch(
          fetchType === 'income' ? `/tag-income/${editingTag.id}` : `/tag-expense/${editingTag.id}`,
          { name: newName },
        );
        queryClient.invalidateQueries({ queryKey: ['tags', fetchType], exact: true });
      }

      setTags(prev => prev.map(t => (t === editingTag ? { ...t, name: newName } : t)));

      showSuccess('Тег обновлен');
      closeEditTagModal();
    } catch (err) {
      showError(
        isAxiosError(err)
          ? (err.response?.data?.message ?? err.message)
          : 'Ошибка при обновлении тега',
      );
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={style}>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 2 }}>
            {isEditMode ? 'Редактировать операцию' : 'Добавить операцию'}
          </Typography>

          {!isEditMode && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
              <Button
                variant={type === 'income' ? 'contained' : 'outlined'}
                color="success"
                disabled={mutation.isPending}
                onClick={() => {
                  setType('income');
                  setSelectedCategory(null);
                  setTags([]);
                  setTemplateQuery('');
                  setSelectedTemplate(null);
                }}
              >
                Доход
              </Button>
              <Button
                variant={type === 'expense' ? 'contained' : 'outlined'}
                color="error"
                disabled={mutation.isPending}
                onClick={() => {
                  setType('expense');
                  setSelectedCategory(null);
                  setTags([]);
                  setTemplateQuery('');
                  setSelectedTemplate(null);
                }}
              >
                Расход
              </Button>
            </Box>
          )}

          {!isEditMode && (
            <Autocomplete
              options={templateOptions}
              loading={templateLoading}
              value={selectedTemplate}
              onChange={async (_, value) => {
                setSelectedTemplate(value);
                await applyOperationTemplate(value);
              }}
              inputValue={templateQuery}
              onInputChange={(_, value) => setTemplateQuery(value.slice(0, SEARCH_LIMITS.max))}
              getOptionLabel={option => option.title}
              isOptionEqualToValue={(option, value) =>
                option.id === value.id && option.kind === value.kind && option.type === value.type
              }
              noOptionsText={
                templateSearch.length < 2 ? 'Введите минимум 2 символа' : 'Ничего не найдено'
              }
              loadingText="Загрузка..."
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{option.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.amount ? formatCurrency(option.amount) : ''}
                      {option.date ? ` • ${new Date(option.date).toLocaleString('ru-RU')}` : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Заполнить по образцу"
                  placeholder="Начните вводить название прошлой операции"
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
            />
          )}
          <TextField
            label="Наименование"
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={() => setTouchedName(true)}
            error={touchedName && !isNameValid}
            helperText={
              touchedName && !isNameValid
                ? `Название должно быть от ${OPERATION_NAME_LIMITS.min} до ${OPERATION_NAME_LIMITS.max} символов`
                : ''
            }
            inputProps={{ maxLength: OPERATION_NAME_LIMITS.max }}
            fullWidth
            sx={{ mb: 2 }}
            disabled={mutation.isPending}
          />

          <TextField
            label="Сумма"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={e => setAmount(sanitizeMoneyInput(e.target.value, 2))}
            onPaste={e => {
              e.preventDefault();
              const text = (e.clipboardData || (window as any).clipboardData).getData('text');
              setAmount(sanitizeMoneyInput(text, 2));
            }}
            onKeyDown={e => {
              const allowedCtrl = [
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'Home',
                'End',
                'Tab',
                'Enter',
              ];
              if (allowedCtrl.includes(e.key)) return;

              const isDigit = e.key >= '0' && e.key <= '9';
              const hasSep = amount.includes('.') || amount.includes(',');
              const isSep = e.key === '.' || e.key === ',';

              if (isDigit) return;
              if (isSep && !hasSep) return;

              e.preventDefault();
            }}
            onBlur={() => {
              if (amount === '.' || amount === '0.' || amount === '') {
                setAmount('0');
              } else {
                setAmount(sanitizeMoneyInput(amount, 2));
              }
              setTouchedAmount(true);
            }}
            error={touchedAmount && !isAmountValid}
            helperText={
              touchedAmount && !isAmountValid ? 'Введите число до 2 знаков после точки' : ''
            }
            fullWidth
            sx={{ mb: 2 }}
            disabled={mutation.isPending}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
            <DateTimePicker
              label="Дата и время"
              value={date}
              onChange={nv => nv && setDate(nv)}
              inputFormat="dd.MM.yyyy HH:mm"
              mask="__.__.____ __:__"
              renderInput={params => (
                <TextField {...params} fullWidth sx={{ mb: 2 }} disabled={mutation.isPending} />
              )}
            />
          </LocalizationProvider>

          <Box
            sx={{
              mb: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: 1,
              borderColor: selectedCategory ? 'primary.main' : 'grey.400',
              borderRadius: 1,
              cursor: mutation.isPending ? 'default' : 'pointer',
            }}
            onClick={() => !mutation.isPending && setOpenCategory(true)}
          >
            <Typography variant="body1" color={selectedCategory ? 'textPrimary' : 'textSecondary'}>
              {selectedCategory ? `Категория: ${selectedCategory.label}` : 'Выбрать категорию'}
            </Typography>
            {selectedCategory && (
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setSelectedCategory(null);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Autocomplete
            multiple
            freeSolo
            options={allTags
              .filter(tag => !tags.some(selected => selected.name === tag.name))
              .map(c => ({ id: c.id, name: c.name }))}
            getOptionLabel={opt => (typeof opt === 'string' ? opt : opt.name)}
            value={tags}
            onChange={(_, v) => {
              const uniqueByName = new Map<string, Tag>();
              v.forEach(item => {
                const tag = typeof item === 'string' ? { id: 0, name: item } : item;
                if (!uniqueByName.has(tag.name)) {
                  uniqueByName.set(tag.name, tag);
                }
              });
              setTags(Array.from(uniqueByName.values()));
            }}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pr: 1,
                }}
              >
                <Typography noWrap sx={{ flexGrow: 1 }}>
                  {option.name}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      openEditTagModal(option);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={async e => {
                      e.stopPropagation();

                      if (option.id !== 0) {
                        try {
                          await api.delete(
                            fetchType === 'income'
                              ? `/tag-income/${option.id}`
                              : `/tag-expense/${option.id}`,
                          );
                          showSuccess('Тег удален');

                          queryClient.invalidateQueries({
                            queryKey: ['tags', fetchType],
                            exact: true,
                          });
                        } catch (err) {
                          showError(
                            isAxiosError(err)
                              ? (err.response?.data?.message ?? err.message)
                              : 'Ошибка при удалении тега',
                          );
                          return;
                        }
                      }

                      setTags(prev => prev.filter(t => t !== option));
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}
            renderTags={(value, getTagProps) =>
              value.map((opt, idx) => {
                const { onDelete, ...rest } = getTagProps({ index: idx });
                return (
                  <Chip
                    {...rest}
                    key={`${opt.name}-${idx}`}
                    label={opt.name}
                    onDelete={() => handleDeleteTag(opt)}
                  />
                );
              })
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Теги"
                placeholder="Добавьте тег"
                fullWidth
                sx={{ mb: 2 }}
                inputProps={{
                  ...params.inputProps,
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      const val = e.currentTarget.value.trim();

                      if (!val) {
                        e.preventDefault();
                        return;
                      }

                      if (val.length < TAG_NAME_LIMITS.min || val.length > TAG_NAME_LIMITS.max) {
                        showError(
                          `Название тега должно быть от ${TAG_NAME_LIMITS.min} до ${TAG_NAME_LIMITS.max} символов`,
                        );
                        e.preventDefault();
                        return;
                      }

                      if (!tags.some(t => t.name === val)) {
                        setTags(prev => [...prev, { id: 0, name: val }]);
                      }

                      e.currentTarget.value = '';
                      e.preventDefault();
                    }
                  },
                }}
                disabled={mutation.isPending}
              />
            )}
            disabled={mutation.isPending}
          />

          <TextField
            label="Примечание"
            value={note}
            onChange={e => setNote(e.target.value)}
            onBlur={() => setTouchedNote(true)}
            error={touchedNote && !isNoteValid}
            helperText={
              touchedNote && !isNoteValid
                ? `Описание не должно превышать ${OPERATION_DESCRIPTION_LIMITS.max} символов`
                : `${note.length}/${OPERATION_DESCRIPTION_LIMITS.max}`
            }
            inputProps={{ maxLength: OPERATION_DESCRIPTION_LIMITS.max }}
            fullWidth
            multiline
            rows={3}
            sx={{ mb: 2 }}
            disabled={mutation.isPending}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid || mutation.isPending}
            >
              {mutation.isPending ? 'Сохранение…' : isEditMode ? 'Сохранить' : 'Добавить'}
            </Button>
            <Button fullWidth variant="outlined" onClick={onClose} disabled={mutation.isPending}>
              Отмена
            </Button>
          </Box>
        </Box>
      </Modal>

      <ModalChooseCategory
        openCategory={openCategory}
        handleCloseCategory={() => setOpenCategory(false)}
        type={fetchType}
        onSelectCategory={(id: string, label: string) => setSelectedCategory({ id, label })}
      />

      <Dialog open={editTagOpen} onClose={closeEditTagModal} fullWidth maxWidth="xs">
        <DialogTitle>Переименовать тег</DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название тега"
            fullWidth
            value={editTagValue}
            onChange={e => setEditTagValue(e.target.value)}
            onBlur={() => setEditTouched(true)}
            error={editTouched && !isEditNameValid}
            helperText={
              editTouched && !isEditNameValid
                ? `Название тега должно быть от ${TAG_NAME_LIMITS.min} до ${TAG_NAME_LIMITS.max} символов`
                : ''
            }
            inputProps={{ maxLength: TAG_NAME_LIMITS.max }}
            disabled={editLoading}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSaveTagName();
              }
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeEditTagModal} disabled={editLoading}>
            Отмена
          </Button>
          <Button
            onClick={handleSaveTagName}
            variant="contained"
            disabled={editLoading || !isEditNameValid}
          >
            {editLoading ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalOperations;
